import React, { useState, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Logo from './Logo'

const TicketConfirmation = () => {
  const { uniqueId } = useParams()
  const [searchParams] = useSearchParams()
  
  // Countdown timer state
  const [timeRemaining, setTimeRemaining] = useState('')
  
  // Handle both correct (?passport=) and incorrect (&passport=) URL formats
  let passportFromUrl = searchParams.get('passport')
  
  // Fallback: check if passport is in the URL hash or malformed query
  if (!passportFromUrl) {
    const currentUrl = window.location.href
    const passportMatch = currentUrl.match(/[?&]passport=([^&]+)/)
    if (passportMatch) {
      passportFromUrl = decodeURIComponent(passportMatch[1])
    }
  }
  
  // Debug logging
  console.log('URL:', window.location.href)
  console.log('Search params:', Object.fromEntries(searchParams))
  console.log('Passport from URL:', passportFromUrl)

  const [loading, setLoading] = useState(true)
  const [registration, setRegistration] = useState(null)
  const [passportNumber, setPassportNumber] = useState(passportFromUrl || '')
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [bagsCount, setBagsCount] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirmAction, setConfirmAction] = useState(null)
  
  // Edit mode states
  const [isEditing, setIsEditing] = useState(false)
  const [editData, setEditData] = useState({
    firstName: '',
    lastName: '',
    birthDate: '',
    passportNumber: '',
    phone: '',
    email: ''
  })
  useEffect(() => {
    if (uniqueId && passportFromUrl) {
      fetchTicketDetails(passportFromUrl)
    } else {
      setLoading(false)
    }
  }, [uniqueId, passportFromUrl])

  // Ensure passport number is updated when URL parameter changes
  useEffect(() => {
    if (passportFromUrl) {
      setPassportNumber(passportFromUrl)
    }
  }, [passportFromUrl])

  // Populate edit data when registration loads
  useEffect(() => {
    if (registration) {
      setEditData({
        firstName: registration.firstName || '',
        lastName: registration.lastName || '',
        birthDate: registration.birthDate || '',
        passportNumber: registration.passportNumber || '',
        phone: registration.phone || '',
        email: registration.email || ''
      })
    }
  }, [registration])

  // Countdown timer to 1pm EST today (June 26th)
  useEffect(() => {
    const targetDate = new Date('2025-06-26T13:00:00-05:00') // June 26, 2025 1:00 PM EST

    const updateCountdown = () => {
      const now = new Date()
      const timeLeft = targetDate - now

      if (timeLeft <= 0) {
        setTimeRemaining('⏰ Deadline has passed')
        return
      }

      const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24))
      const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000)

      if (days > 0) {
        setTimeRemaining(`${days}d ${hours}h ${minutes}m ${seconds}s`)
      } else {
        setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`)
      }
    }

    updateCountdown()
    const interval = setInterval(updateCountdown, 1000)

    return () => clearInterval(interval)
  }, [])

  const fetchTicketDetails = async (passport) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/ticket/${uniqueId}?passport=${encodeURIComponent(passport)}`)
      const data = await response.json()

      if (data.status === 'success') {
        setRegistration(data.registration)
        setBagsCount(data.registration.bagsCount || 1)
        setIsAuthenticated(true)
      } else {
        toast.error(data.message || 'Ticket not found')
      }
    } catch (error) {
      console.error('Error fetching ticket details:', error)
      toast.error('Error loading ticket details')
    } finally {
      setLoading(false)
    }
  }

  const handlePassportSubmit = async (e) => {
    e.preventDefault()
    if (!passportNumber.trim()) {
      toast.error('Please enter your passport number')
      return
    }

    setLoading(true)
    try {
      await fetchTicketDetails(passportNumber.trim())
    } catch (error) {
      console.error('Error verifying passport:', error)
      toast.error('Error verifying access')
    }
  }

  const handleAction = (action) => {
    setConfirmAction(action)
    setShowConfirmModal(true)
  }

  const confirmTicketAction = async () => {
    setIsSubmitting(true)
    setShowConfirmModal(false)

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/ticket-confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uniqueId,
          passportNumber,
          action: confirmAction,
          bagsCount: confirmAction === 'confirm' ? bagsCount : undefined
        })
      })

      const data = await response.json()

      if (data.status === 'success') {
        if (confirmAction === 'confirm') {
          toast.success('Your ticket has been confirmed successfully! We will contact you with further details.')
        } else {
          toast.success('Your ticket has been cancelled. Your spot will be released to the waitlist.')
        }
        // Update the registration status locally
        setRegistration(prev => ({
          ...prev,
          adminStatus: data.newStatus
        }))
      } else {
        toast.error(data.message || 'Error processing your request')
      }
    } catch (error) {
      console.error('Error confirming ticket:', error)
      toast.error('Error processing your request')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEditData = () => {
    setIsEditing(true)
  }

  const handleEditCancel = () => {
    setIsEditing(false)
    // Reset edit data to original values
    setEditData({
      firstName: registration.firstName || '',
      lastName: registration.lastName || '',
      birthDate: registration.birthDate || '',
      passportNumber: registration.passportNumber || '',
      phone: registration.phone || '',
      email: registration.email || ''
    })
  }

  const handleEditSave = async () => {
    setIsSubmitting(true)
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/ticket-update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uniqueId,
          passportNumber,
          updateData: editData
        })
      })

      const data = await response.json()

      if (data.status === 'success') {
        toast.success('Your information has been updated successfully!')
        
        // Check if passport number was changed
        const passportChanged = editData.passportNumber !== registration.passportNumber
        
        // Update the registration data locally
        setRegistration(prev => ({
          ...prev,
          firstName: editData.firstName,
          lastName: editData.lastName,
          birthDate: editData.birthDate,
          passportNumber: editData.passportNumber,
          phone: editData.phone,
          email: editData.email
        }))
        
        // If passport number changed, update local state and URL to prevent lockout
        if (passportChanged) {
          setPassportNumber(editData.passportNumber)
          
          // Update URL query parameter to reflect new passport number
          const url = new URL(window.location.href)
          url.searchParams.set('passport', editData.passportNumber)
          window.history.replaceState({}, '', url.toString())
          
          console.log('Passport number updated - new number:', editData.passportNumber)
        }
        
        setIsEditing(false)
      } else {
        toast.error(data.message || 'Error updating your information')
      }
    } catch (error) {
      console.error('Error updating data:', error)
      toast.error('Error updating your information')
    } finally {
      setIsSubmitting(false)
    }
  }

  const getBagCost = () => {
    if (bagsCount <= 1) return 1175 // Base fare only
    if (bagsCount === 2) return 1175 + 120 // Base + 1 extra
    if (bagsCount === 3) return 1175 + 120 + 200 // Base + 1 extra + 1 more
    if (bagsCount === 4) return 1175 + 120 + 200 + 200 // Base + 1 extra + 2 more
    return 1175
  }

  const getBagDescription = () => {
    if (bagsCount === 1) return 'Base fare only (1 bag included)'
    if (bagsCount === 2) return 'Base fare + 1 additional bag'
    if (bagsCount === 3) return 'Base fare + 2 additional bags'
    if (bagsCount === 4) return 'Base fare + 3 additional bags'
    return 'Base fare only'
  }

  const getFlightDisplayInfo = (preference) => {
    // Simple message about flight dates - no specific times or details
    return {
      date: 'Your flight will be either June 29th or 30th - exact flight date and time will be confirmed.',
      departure: 'Flight details will be confirmed',
      arrival: 'Flight details will be confirmed'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading ticket details...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="flight-header py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <Logo className="h-16 w-auto" />
              <div className="text-left">
                <h1 className="text-4xl font-bold mb-1">Igud Hayeshivos</h1>
                <h2 className="text-2xl font-semibold">Flight Confirmation</h2>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {!isAuthenticated ? (
          <div className="card p-8">
            <h3 className="text-2xl font-semibold text-gray-800 mb-6">Access Verification Required</h3>
            <p className="text-gray-600 mb-6">
              Please enter your passport number to access your ticket confirmation.
            </p>
            
            <form onSubmit={handlePassportSubmit} className="space-y-4">
              <div>
                <label htmlFor="passport" className="block text-sm font-medium text-gray-700 mb-2">
                  Passport Number *
                </label>
                <input
                  type="text"
                  id="passport"
                  value={passportNumber}
                  onChange={(e) => setPassportNumber(e.target.value)}
                  className="input w-full max-w-md"
                  placeholder="Enter your passport number"
                  required
                />
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? 'Verifying...' : 'Access Ticket'}
              </button>
            </form>
          </div>
        ) : registration ? (
          <div className="space-y-8">
            {/* Status Check */}
            {registration.adminStatus === 'confirmed' && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                <strong>✅ Confirmed:</strong> Your ticket has been confirmed! We will contact you with further details.
              </div>
            )}
            
            {registration.adminStatus === 'rejected' && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                <strong>🛑 Cancelled:</strong> Your ticket has been cancelled. Your spot has been released to the waitlist.
              </div>
            )}

            {registration.adminStatus === 'accepted' && (
              <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
                <strong>⏰ Action Required:</strong> Please confirm your flight details before deadline.
              </div>
            )}

            {/* Countdown Timer - Only show for accepted users */}
            {registration.adminStatus === 'accepted' && (
              <div className="bg-red-50 border border-red-300 text-red-800 px-4 py-3 rounded mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <strong>🚨 Confirmation Deadline:</strong>
                    <div className="text-sm mt-1">Thursday, June 26th at 1:00 PM EST</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-mono font-bold">{timeRemaining}</div>
                    <div className="text-xs">Time Remaining</div>
                  </div>
                </div>
              </div>
            )}

            {/* Main Content */}
            <div className="card p-8">
              <h3 className="text-2xl font-semibold text-gray-800 mb-6">
                {registration.adminStatus === 'confirmed' ? 'Flight Information' : 'Flight Confirmation Required'}
              </h3>
              
              <p className="text-gray-600 mb-6">
                Dear {registration.firstName} {registration.lastName},
              </p>
              
              {registration.adminStatus === 'confirmed' ? (
                <p className="text-gray-600 mb-6">
                  Your flight has been confirmed! Below are your flight details for reference.
                  If you need to make any changes, please contact us directly.
                </p>
              ) : (
                <>
                  <p className="text-gray-600 mb-6">
                    Thank you for registering for one of our upcoming flights. Before we issue your ticket,
                    we need your confirmation on the following information and your agreement to the terms and conditions.
                  </p>
                  
                  <p className="text-gray-700 font-medium mb-6">
                    Please review your personal and flight details below:
                  </p>
                </>
              )}

              {/* Passenger Details */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-semibold text-gray-800">Passenger Details</h4>
                  {!isEditing && registration.adminStatus === 'accepted' && registration.adminStatus !== 'confirmed' && (
                    <button
                      onClick={handleEditData}
                      disabled={isSubmitting}
                      className="btn bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm"
                    >
                      ✏️ Edit Details
                    </button>
                  )}
                </div>
                
                {isEditing ? (
                  <div className="bg-blue-50 border border-blue-300 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                        <input
                          type="text"
                          value={editData.firstName}
                          onChange={(e) => setEditData(prev => ({ ...prev, firstName: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={isSubmitting}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                        <input
                          type="text"
                          value={editData.lastName}
                          onChange={(e) => setEditData(prev => ({ ...prev, lastName: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth *</label>
                        <input
                          type="text"
                          value={editData.birthDate}
                          onChange={(e) => setEditData(prev => ({ ...prev, birthDate: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="DD/MM/YYYY"
                          disabled={isSubmitting}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Passport Number *</label>
                        <input
                          type="text"
                          value={editData.passportNumber}
                          onChange={(e) => setEditData(prev => ({ ...prev, passportNumber: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                        <input
                          type="tel"
                          value={editData.phone}
                          onChange={(e) => setEditData(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., +1-555-123-4567"
                          disabled={isSubmitting}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                        <input
                          type="email"
                          value={editData.email}
                          onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="your.email@example.com"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <button
                        onClick={handleEditSave}
                        disabled={isSubmitting}
                        className="btn bg-green-600 hover:bg-green-700 text-white px-4 py-2"
                      >
                        {isSubmitting ? 'Saving...' : '✅ Save Changes'}
                      </button>
                      <button
                        onClick={handleEditCancel}
                        disabled={isSubmitting}
                        className="btn bg-gray-600 hover:bg-gray-700 text-white px-4 py-2"
                      >
                        ❌ Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-300">
                      <tbody>
                        <tr className="border-b">
                          <td className="px-4 py-3 bg-gray-50 font-medium text-gray-700">Full Name</td>
                          <td className="px-4 py-3">{registration.firstName} {registration.lastName}</td>
                        </tr>
                        <tr className="border-b">
                          <td className="px-4 py-3 bg-gray-50 font-medium text-gray-700">Date of Birth</td>
                          <td className="px-4 py-3">{registration.birthDate}</td>
                        </tr>
                        <tr className="border-b">
                          <td className="px-4 py-3 bg-gray-50 font-medium text-gray-700">Passport Number</td>
                          <td className="px-4 py-3">{registration.passportNumber}</td>
                        </tr>
                        <tr className="border-b">
                          <td className="px-4 py-3 bg-gray-50 font-medium text-gray-700">Phone Number</td>
                          <td className="px-4 py-3">{registration.phone}</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 bg-gray-50 font-medium text-gray-700">Email Address</td>
                          <td className="px-4 py-3">{registration.email}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Reviewer Comment Section - Only show if AI validation status is invalid */}
              {registration.aiValidationStatus === 'invalid' && registration.aiValidationComment && (
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Reviewer Comment</h4>
                  <div className="bg-red-50 border border-red-300 p-4 rounded-lg">
                    <div className="text-red-800">
                      {(() => {
                        try {
                          // Try to parse as JSON first (AI validation result)
                          const parsedComment = JSON.parse(registration.aiValidationComment);
                          return parsedComment.comments || registration.aiValidationComment;
                        } catch {
                          // If not JSON, display as plain text
                          return registration.aiValidationComment;
                        }
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* Flight Details */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Flight Details</h4>
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                  <p className="text-blue-700">
                    {getFlightDisplayInfo(registration.flightPreference).date}
                  </p>
                </div>
              </div>

              {/* Cost Summary and Baggage Selection */}
              {registration.adminStatus === 'accepted' && (
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Baggage Selection & Cost Summary</h4>
                  
                  <div className="mb-6">
                    <label htmlFor="bags" className="block text-lg font-semibold text-gray-800 mb-3">
                      🧳 Select Number of Bags *
                    </label>
                    <div className="relative max-w-md">
                      <select
                        id="bags"
                        value={bagsCount}
                        onChange={(e) => setBagsCount(parseInt(e.target.value))}
                        className="w-full px-4 py-3 pr-12 text-lg font-medium bg-white border-2 border-blue-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none cursor-pointer hover:border-blue-400 transition-colors duration-200 appearance-none"
                        disabled={isSubmitting}
                      >
                        <option value={1}>1 bag (Base fare only) - $1,175</option>
                        <option value={2}>2 bags (Base + 1 extra) - $1,295</option>
                        <option value={3}>3 bags (Base + 2 extra) - $1,495</option>
                        <option value={4}>4 bags (Base + 3 extra) - $1,695</option>
                      </select>
                      {/* Custom dropdown arrow */}
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-blue-600 font-medium">
                      💡 Click above to change your baggage selection and see updated pricing
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-300">
                      <tbody>
                        <tr className="border-b">
                          <td className="px-4 py-3 bg-gray-50 font-medium text-gray-700">Base Fare</td>
                          <td className="px-4 py-3">$1,175</td>
                        </tr>
                        {bagsCount > 1 && (
                          <tr className="border-b">
                            <td className="px-4 py-3 bg-gray-50 font-medium text-gray-700">Additional Bags</td>
                            <td className="px-4 py-3">
                              {bagsCount === 2 && '$120 (1 extra bag)'}
                              {bagsCount === 3 && '$320 (2 extra bags)'}
                              {bagsCount === 4 && '$520 (3 extra bags)'}
                            </td>
                          </tr>
                        )}
                        <tr className="bg-blue-50">
                          <td className="px-4 py-3 font-bold text-gray-800">Total Cost</td>
                          <td className="px-4 py-3 font-bold text-blue-600">${getBagCost()}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Important Notes */}
              {registration.adminStatus === 'accepted' && (<div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
                <div className="flex">
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong>Important:</strong> All additional bags must be selected and prepaid at this stage. 
                      We will not be able to add baggage after ticketing, and the airport will be very busy.
                    </p>
                    <p className="text-sm text-yellow-700 mt-2">
                      <strong>Note:</strong> Once we process your ticket, it is not refundable.
                    </p>
                  </div>
                </div>
              </div>)}

              {/* Action Buttons */}
              {registration.adminStatus === 'accepted' && (
                <div className="text-center space-y-4">
                  {/* Fully Booked Notice */}
                  <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg mb-6">
                    <div className="flex items-center justify-center mb-2">
                      <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <strong className="text-lg">All Flights Are Fully Booked</strong>
                    </div>
                    <p className="text-red-600 font-medium">
                      We regret to inform you that all available flights are now fully booked.
                      Unfortunately, we are no longer able to accept new confirmations at this time.
                    </p>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button
                      disabled={true}
                      className="btn bg-gray-400 text-gray-600 px-8 py-3 text-lg cursor-not-allowed opacity-50"
                      title="All flights are fully booked"
                    >
                      ✅ Confirm My Spot & Baggage (Unavailable)
                    </button>
                    
                    <button
                      onClick={() => handleAction('cancel')}
                      disabled={isSubmitting}
                      className="btn bg-red-600 hover:bg-red-700 text-white px-8 py-3 text-lg"
                    >
                      🛑 I'm No Longer Interested
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="card p-8 text-center">
            <h3 className="text-2xl font-semibold text-red-600 mb-4">Access Denied</h3>
            <p className="text-gray-600">
              The ticket you're looking for was not found or you don't have permission to access it.
            </p>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h4 className="text-lg font-semibold mb-4">
              {confirmAction === 'confirm' ? 'Confirm Your Ticket' : 'Cancel Your Ticket'}
            </h4>
            
            <p className="text-gray-600 mb-6">
              {confirmAction === 'confirm' 
                ? `Are you sure you want to confirm your ticket with ${bagsCount} bag${bagsCount > 1 ? 's' : ''} for $${getBagCost()}? This action cannot be undone.`
                : 'Are you sure you want to cancel your ticket? Your spot will be released to the waitlist.'
              }
            </p>
            
            <div className="flex gap-4">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="btn btn-secondary flex-1"
                disabled={isSubmitting}
              >
                No, Go Back
              </button>
              <button
                onClick={confirmTicketAction}
                className={`btn flex-1 ${confirmAction === 'confirm' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white`}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Processing...' : `Yes, ${confirmAction === 'confirm' ? 'Confirm' : 'Cancel'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8 mt-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h4 className="text-lg font-semibold mb-2">Igud Hayeshivos</h4>
          <p className="text-gray-300">
            For questions about your ticket, please contact us at{' '}
            <a href="mailto:flights@igudys.com" className="text-blue-400 hover:text-blue-300">
              flights@igudys.com
            </a>
          </p>
        </div>
      </footer>
    </div>
  )
}

export default TicketConfirmation